import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Loader2, CheckCircle, AlertCircle, Sparkles, Paperclip, FileText, FileSpreadsheet, File } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PROMPT_START = '--- PROMPT PARA TU ASISTENTE ---';
const PROMPT_END = '--- FIN DEL PROMPT ---';

function extractPromptBlock(text) {
  const startIdx = text.indexOf(PROMPT_START);
  const endIdx = text.indexOf(PROMPT_END);
  if (startIdx === -1 || endIdx === -1 || endIdx <= startIdx) return null;
  const prompt = text.substring(startIdx + PROMPT_START.length, endIdx).trim();
  const before = text.substring(0, startIdx).trim();
  const after = text.substring(endIdx + PROMPT_END.length)
    .replace('[REEMBOLSO_APROBADO]', '').replace('[REEMBOLSO_DENEGADO]', '').trim();
  return { prompt, before, after };
}

function hasRefundApproved(text) {
  return text.includes('[REEMBOLSO_APROBADO]');
}

function hasRefundDenied(text) {
  return text.includes('[REEMBOLSO_DENEGADO]');
}

function cleanMarkers(text) {
  return text.replace('[REEMBOLSO_APROBADO]', '').replace('[REEMBOLSO_DENEGADO]', '').trim();
}

const FileIcon = ({ name }) => {
  const ext = (name || '').split('.').pop()?.toLowerCase();
  if (ext === 'pdf') return <FileText className="w-4 h-4 text-red-500" />;
  if (ext === 'xlsx') return <FileSpreadsheet className="w-4 h-4 text-emerald-600" />;
  return <File className="w-4 h-4 text-gray-500" />;
};

const SupportChat = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { type: 'alex', text: 'Hola! Soy Alex del equipo de ACBotHub. Estoy aqui para ayudarte a configurar tu asistente de WhatsApp. Cuentame, en que te puedo ayudar?' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [sessionId] = useState(() => {
    const stored = localStorage.getItem('alex_session_id');
    if (stored) return stored;
    const newId = `alex_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('alex_session_id', newId);
    return newId;
  });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);
  useEffect(() => { if (isOpen) inputRef.current?.focus(); }, [isOpen]);

  useEffect(() => {
    const handler = () => setIsOpen(true);
    window.addEventListener('openSupportChat', handler);
    return () => window.removeEventListener('openSupportChat', handler);
  }, []);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading || uploading) return;
    setInput('');
    setMessages(prev => [...prev, { type: 'user', text }]);
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/support-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: text, session_id: sessionId })
      });
      const data = await res.json();
      if (data.response) {
        setMessages(prev => [...prev, { type: 'alex', text: data.response }]);
      }
    } catch {
      setMessages(prev => [...prev, { type: 'alex', text: 'Disculpa, hubo un error de conexion. Intenta de nuevo en un momento.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';

    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['pdf', 'xlsx', 'txt'].includes(ext)) {
      setMessages(prev => [...prev, { type: 'system', text: 'Formato no soportado. Usa PDF, Excel (.xlsx) o TXT.' }]);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setMessages(prev => [...prev, { type: 'system', text: 'El archivo es demasiado grande. Maximo 10 MB.' }]);
      return;
    }

    // Show file in chat
    setMessages(prev => [...prev, { type: 'user', text: `📎 ${file.name}`, isFile: true, fileName: file.name }]);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('session_id', sessionId);

      const res = await fetch(`${API_URL}/api/support-chat/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Error al subir el archivo');
      }

      const data = await res.json();
      if (data.alex_response) {
        setMessages(prev => [...prev, { type: 'alex', text: data.alex_response }]);
      } else {
        setMessages(prev => [...prev, {
          type: 'alex',
          text: `Recibi tu documento "${file.name}". Dejame analizarlo para generar un prompt para tu asistente...`
        }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, { type: 'system', text: err.message || 'Error al procesar el documento.' }]);
    } finally {
      setUploading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const handleApplyPrompt = async (prompt) => {
    if (!user) {
      // Save prompt to localStorage so it can be applied after registration
      localStorage.setItem('pending_prompt', prompt);
      setIsOpen(false);
      navigate('/register');
      return;
    }
    setActionLoading('apply');
    try {
      const res = await fetch(`${API_URL}/api/bot/apply-prompt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ prompt })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setMessages(prev => [...prev, { type: 'system', text: 'Prompt aplicado exitosamente! Abriendo configuracion del asistente...' }]);
        const botId = data.bot_id;
        setTimeout(() => {
          if (botId) {
            navigate(`/dashboard?bot_id=${botId}&view=config`);
          } else {
            navigate('/dashboard');
          }
          setIsOpen(false);
        }, 1200);
      } else {
        setMessages(prev => [...prev, { type: 'system', text: data.detail || 'Error al aplicar el prompt. Intenta de nuevo.' }]);
      }
    } catch {
      setMessages(prev => [...prev, { type: 'system', text: 'Error de conexion al aplicar el prompt.' }]);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRefund = async () => {
    if (!user) return;
    setActionLoading('refund');
    try {
      const res = await fetch(`${API_URL}/api/billing/process-refund`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setMessages(prev => [...prev, { type: 'system', text: data.message }]);
      } else {
        setMessages(prev => [...prev, { type: 'system', text: data.detail || 'No se pudo procesar el reembolso.' }]);
      }
    } catch {
      setMessages(prev => [...prev, { type: 'system', text: 'Error de conexion al procesar el reembolso.' }]);
    } finally {
      setActionLoading(null);
    }
  };

  const newSession = () => {
    const newId = `alex_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('alex_session_id', newId);
    setMessages([
      { type: 'alex', text: 'Hola! Soy Alex del equipo de ACBotHub. Estoy aqui para ayudarte a configurar tu asistente de WhatsApp. Cuentame, en que te puedo ayudar?' }
    ]);
    window.location.reload();
  };

  const renderMessage = (msg, idx) => {
    if (msg.type === 'system') {
      return (
        <div key={idx} className="flex justify-center" data-testid={`support-msg-${idx}`}>
          <div className="bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-xl px-4 py-2 text-xs flex items-center gap-2 max-w-[90%]">
            <CheckCircle className="w-3.5 h-3.5 shrink-0" />
            <span>{msg.text}</span>
          </div>
        </div>
      );
    }

    if (msg.type === 'user') {
      return (
        <div key={idx} className="flex justify-end" data-testid={`support-msg-${idx}`}>
          {msg.isFile ? (
            <div className="max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed bg-indigo-600 text-white rounded-br-md flex items-center gap-2">
              <FileIcon name={msg.fileName} />
              <span className="truncate">{msg.fileName}</span>
            </div>
          ) : (
            <div className="max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed bg-indigo-600 text-white rounded-br-md"
              style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {msg.text}
            </div>
          )}
        </div>
      );
    }

    // Alex message — parse for special blocks
    const promptData = extractPromptBlock(msg.text);
    const refundApproved = hasRefundApproved(msg.text);
    const refundDenied = hasRefundDenied(msg.text);
    const cleanText = cleanMarkers(promptData ? '' : msg.text);

    return (
      <div key={idx} className="flex justify-start flex-col gap-2" data-testid={`support-msg-${idx}`}>
        {/* Regular text before prompt */}
        {promptData && promptData.before && (
          <div className="max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-md"
            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {promptData.before}
          </div>
        )}

        {/* Prompt block */}
        {promptData && (
          <div className="max-w-[90%] rounded-xl overflow-hidden border border-indigo-200 shadow-sm" data-testid={`prompt-block-${idx}`}>
            <div className="bg-indigo-50 px-3 py-2 flex items-center gap-2 border-b border-indigo-200">
              <Sparkles className="w-4 h-4 text-indigo-600" />
              <span className="text-xs font-semibold text-indigo-700">Prompt generado para tu asistente</span>
            </div>
            <div className="bg-white px-3 py-2.5 text-xs text-gray-700 font-mono leading-relaxed max-h-40 overflow-y-auto"
              style={{ whiteSpace: 'pre-wrap' }}>
              {promptData.prompt}
            </div>
            <div className="bg-indigo-50 px-3 py-2 border-t border-indigo-200">
              <button
                data-testid={`apply-prompt-btn-${idx}`}
                onClick={() => handleApplyPrompt(promptData.prompt)}
                disabled={actionLoading === 'apply'}
                className="w-full bg-indigo-600 text-white text-xs font-semibold py-2 px-4 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
              >
                {actionLoading === 'apply' ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin" />Aplicando...</>
                ) : user ? (
                  <><CheckCircle className="w-3.5 h-3.5" />Aplicar este prompt a mi asistente</>
                ) : (
                  <><Sparkles className="w-3.5 h-3.5" />Registrarme para activar mi asistente</>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Text after prompt */}
        {promptData && promptData.after && (
          <div className="max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-md"
            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {promptData.after}
          </div>
        )}

        {/* Regular message (no prompt) */}
        {!promptData && (
          <div className="max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-md"
            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {cleanText}
          </div>
        )}

        {/* Refund approved button */}
        {refundApproved && user && (
          <div className="max-w-[80%]" data-testid={`refund-btn-${idx}`}>
            <button
              onClick={handleRefund}
              disabled={actionLoading === 'refund'}
              className="w-full bg-red-500 text-white text-xs font-semibold py-2.5 px-4 rounded-xl hover:bg-red-600 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
            >
              {actionLoading === 'refund' ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" />Procesando reembolso...</>
              ) : (
                <><AlertCircle className="w-3.5 h-3.5" />Confirmar reembolso</>
              )}
            </button>
          </div>
        )}

        {/* Refund denied info */}
        {refundDenied && (
          <div className="max-w-[80%] bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-700">
            Contacto: soporte@bothub.com
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {!isOpen && (
        <button
          data-testid="support-chat-fab"
          onClick={() => setIsOpen(true)}
          className="fixed bottom-20 right-6 z-[9999] w-14 h-14 rounded-full bg-indigo-600 text-white shadow-xl flex items-center justify-center hover:bg-indigo-700 transition-all duration-300 hover:scale-105"
          style={{ animation: 'supportPulse 3s ease-in-out infinite' }}
        >
          <MessageCircle className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-400 rounded-full border-2 border-white" />
        </button>
      )}

      {isOpen && (
        <div
          data-testid="support-chat-panel"
          className="fixed bottom-20 right-6 z-[9999] w-[380px] max-w-[calc(100vw-2rem)] rounded-2xl shadow-2xl overflow-hidden flex flex-col"
          style={{
            height: 'min(540px, calc(100vh - 6rem))',
            animation: 'chatSlideIn 0.3s ease-out',
            border: '1px solid rgba(79,70,229,0.15)'
          }}
        >
          <div className="bg-indigo-600 px-5 py-4 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center text-white font-bold text-sm">A</div>
              <div>
                <div className="text-white font-semibold text-sm">Alex - Soporte ACBotHub</div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-emerald-400 rounded-full inline-block" />
                  <span className="text-indigo-100 text-xs">En linea</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button data-testid="support-chat-new-session" onClick={newSession}
                className="text-indigo-200 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors text-xs" title="Nueva conversacion">
                Nueva
              </button>
              <button data-testid="support-chat-close" onClick={() => setIsOpen(false)}
                className="text-indigo-200 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto bg-gray-50 px-4 py-4 space-y-3" style={{ scrollBehavior: 'smooth' }}>
            {messages.map((msg, i) => renderMessage(msg, i))}
            {(loading || uploading) && (
              <div className="flex justify-start" data-testid="support-chat-loading">
                <div className="bg-white text-gray-500 shadow-sm border border-gray-100 rounded-2xl rounded-bl-md px-4 py-2.5 text-sm flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {uploading ? 'Alex esta analizando tu documento...' : 'Alex esta escribiendo...'}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Upload hint banner */}
          {messages.length <= 2 && (
            <div className="bg-indigo-50 border-t border-indigo-100 px-4 py-2 shrink-0">
              <p className="text-xs text-indigo-600 flex items-center gap-1.5">
                <Paperclip className="w-3 h-3" />
                Sube un PDF, Excel o TXT con info de tu negocio para generar un prompt automatico
              </p>
            </div>
          )}

          <div className="bg-white border-t border-gray-200 px-4 py-3 shrink-0">
            <div className="flex items-center gap-2">
              {/* File upload button */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.xlsx,.txt"
                onChange={handleFileUpload}
                className="hidden"
                data-testid="support-chat-file-input"
              />
              <button
                data-testid="support-chat-upload-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={loading || uploading}
                className="w-10 h-10 rounded-xl bg-gray-100 text-gray-500 flex items-center justify-center hover:bg-indigo-50 hover:text-indigo-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all shrink-0"
                title="Subir documento (PDF, Excel, TXT)"
              >
                <Paperclip className="w-4 h-4" />
              </button>

              <input data-testid="support-chat-input" ref={inputRef} type="text" value={input}
                onChange={(e) => setInput(e.target.value.slice(0, 2000))} onKeyDown={handleKeyDown}
                placeholder="Escribe tu mensaje..." disabled={loading || uploading}
                className="flex-1 bg-gray-100 border-0 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:bg-white transition-all placeholder:text-gray-400" />
              <button data-testid="support-chat-send" onClick={sendMessage} disabled={loading || uploading || !input.trim()}
                className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all shrink-0">
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes supportPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(79, 70, 229, 0.4); }
          50% { box-shadow: 0 0 0 12px rgba(79, 70, 229, 0); }
        }
        @keyframes chatSlideIn {
          from { opacity: 0; transform: translateY(16px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </>
  );
};

export default SupportChat;
